import { useState } from 'react'
import {
    FormFieldsModel,
    ModelAnswerFromProps,
    SecondaryActionsModel,
} from '../models/StoreDetailModel'

interface UseFieldsVisibilityParams {
    fields: FormFieldsModel[]
    answers?: ModelAnswerFromProps[]
}

// Réplica, a nivel de fields (FieldsView), de la lógica de hide_questions/
// show_questions que ExecutionView usa entre preguntas: un field con
// `status === 3` arranca oculto y se muestra/oculta según su _id "real" (ver
// raw_id), cuando otro field "controlador" (SiNoNa, Dropdown o
// DropdownMultipleSelect) de la misma lista guarda una respuesta cuya
// secondary_action seleccionada trae hide_fields/show_fields.
//
// Parte pura del cálculo (sin el estado local optimista, que sólo tiene
// sentido dentro del render en vivo de FieldsView). Se usa desde el hook de
// abajo y también desde ExecutionView.validateAndGetIncomplete, que necesita
// saber qué fields de una pregunta type 14 están ocultos antes de exigirles
// respuesta a los que son `required`.
export const computeHiddenFieldsMap = (
    fields: FormFieldsModel[],
    answers?: ModelAnswerFromProps[],
    localAnswers?: Record<string, ModelAnswerFromProps>,
): Record<string, boolean> => {
    const getSavedAnswer = (
        field: FormFieldsModel,
    ): ModelAnswerFromProps | undefined => {
        if (!field._id) return undefined
        return (
            localAnswers?.[field._id] ??
            (answers ?? []).find((a) => a && a._id === field._id)
        )
    }

    // Se matchea primero por field_text (label de la opción): Dropdown y
    // SiNoNa siempre lo setean con el label exacto de LO QUE ACABAN DE TOCAR.
    // Es más confiable que option_selected_id porque SiNoNaField, cuando no
    // hay saveItemSteps, arma su answer copiando option_selected_id/text
    // directamente de answerProp (el answer de la pregunta padre, para no
    // pisarlos al persistir merged en la misma fila) — ese valor es el de la
    // pregunta padre, no el de la opción tocada en este field, así que
    // matchear por ahí primero daría el field "congelado" en la selección
    // inicial en vez de reaccionar a cada click.
    // Sólo caemos a option_selected_id cuando field_text no matchea ninguna
    // opción de este field — típicamente DropdownMultipleSelect con más de
    // una opción tocada (field_text es un join de labels, no matchea una sola
    // opción), donde vale la última seleccionada.
    const getSelectedAction = (
        field: FormFieldsModel,
    ): SecondaryActionsModel | undefined => {
        const savedAnswer = getSavedAnswer(field)
        if (!savedAnswer || !field.secondary_actions?.length) return undefined

        if (savedAnswer.field_text) {
            const byLabel = field.secondary_actions.find(
                (a) => a.label === savedAnswer.field_text,
            )
            if (byLabel) return byLabel
        }

        if (
            savedAnswer.option_selected_id &&
            savedAnswer.option_selected_id.length > 0
        ) {
            const lastSelectedId =
                savedAnswer.option_selected_id[
                    savedAnswer.option_selected_id.length - 1
                ]
            return field.secondary_actions.find(
                (action) => action._id === lastSelectedId,
            )
        }

        return undefined
    }

    // hide_fields/show_fields referencian el raw_id (el _id original que manda
    // el back), no el _id sintético que le asigna withSyntheticIds para poder
    // guardar la respuesta sin colisionar entre preguntas.
    const targetKey = (field: FormFieldsModel): string | undefined =>
        field.raw_id ?? field._id

    const hiddenMap: Record<string, boolean> = {}
    fields.forEach((field) => {
        const key = targetKey(field)
        if (key) {
            hiddenMap[key] = field.status === 3
        }
    })
    fields.forEach((field) => {
        const action = getSelectedAction(field)
        if (!action) return
        // Un field no puede ocultarse/mostrarse a sí mismo: si su propia
        // secondary_action seleccionada se referencia a sí misma (p. ej. datos
        // de prueba copiados de otro field controlador), se ignora — si no, al
        // procesarse en el mismo pase pisa lo que haya decidido el controlador
        // real, según el orden en el array de `fields`.
        const selfKey = targetKey(field)
        ;(action.hide_fields ?? action.hide_questions ?? []).forEach((id) => {
            if (id === selfKey) return
            hiddenMap[id] = true
        })
        ;(action.show_fields ?? action.show_questions ?? []).forEach((id) => {
            if (id === selfKey) return
            hiddenMap[id] = false
        })
    })

    return hiddenMap
}

const targetFieldKey = (field: FormFieldsModel): string | undefined =>
    field.raw_id ?? field._id

// Igual que en usePonderateGroup: `answers` (prop) tarda un ciclo en reflejar
// lo recién guardado, así que guardamos una copia local optimista por field
// para que el show/hide reaccione al toque y no a la siguiente recarga.
// `onFieldAnswered` se pasa a los fields controladores como un callback
// DEDICADO (`onAnswerChange`), separado de checkComplete/saveItemSteps: así
// el toque siempre actualiza la visibilidad en el momento, sin depender de
// que checkComplete se termine invocando (algunos flujos, como FormSteps,
// usan saveItemSteps en su lugar y nunca llaman a checkComplete).
export const useFieldsVisibility = ({
    fields,
    answers,
}: UseFieldsVisibilityParams) => {
    const [localAnswers, setLocalAnswers] = useState<
        Record<string, ModelAnswerFromProps>
    >({})

    const hiddenMap = computeHiddenFieldsMap(fields, answers, localAnswers)

    const isFieldHidden = (field: FormFieldsModel): boolean => {
        const key = targetFieldKey(field)
        return !!key && hiddenMap[key] === true
    }

    // Callback dedicado para los fields "controladores": guarda la respuesta
    // localmente (optimista) apenas se toca la opción, así el show/hide del
    // resto de los fields se recalcula ya en el próximo render, sin esperar a
    // que `answers` (prop) se actualice desde afuera ni depender de que
    // checkComplete/saveItemSteps efectivamente se invoquen.
    const onFieldAnswered =
        (field: FormFieldsModel) => (answer?: ModelAnswerFromProps) => {
            if (answer && field._id && field.secondary_actions?.length) {
                setLocalAnswers((prev) => ({ ...prev, [field._id]: answer }))
            }
        }

    return { isFieldHidden, onFieldAnswered }
}
